import { z } from "zod";
import { MAX_GESTURE_POINT_VALUES, MAX_SESSION_BASE64_CHARS } from "./elements.ts";
import { CapSchema } from "./capabilities.ts";
import { EventKindSchema, EventPayloadSchema, MAX_SUBSCRIBE_TOPICS } from "./events.ts";
import {
  MachinePathSchema,
  TerminalCwdSchema,
  TerminalExitReasonSchema,
  TerminalGeometrySchema,
  TerminalProgramSchema,
  TerminalReadinessSchema,
} from "./machine.ts";
import { TerminalRuntimeSchema } from "./jobs.ts";
import { SessionRefSchema } from "./session-ref.ts";
import {
  CarrySchema,
  GestureKindSchema,
  PresencePayloadSchema,
  PresenceStateSchema,
} from "./presence.ts";
import { PluginIdSchema, PluginRosterSchema } from "./plugin.ts";
import { PrincipalSchema } from "./principal.ts";
import { ManifoldRefSchema } from "./uri.ts";
import { STREAM_CLIENT_BODIES, STREAM_SERVER_BODIES } from "./stream.ts";

/**
 * Session channel (`/ws/session`): browsers, SDKs, tools. JSON text frames.
 *
 * FRAME GRAMMAR (v36) — one socket per tab, many rooms or one roomless observer. Every
 * frame is either connection-level or channel-level:
 *
 *   connection-level   client → server  {"type":"observe","token":"…","protocolVersion":37}
 *                      client → server  {"type":"pong"}
 *                      client → server  {"type":"subscribe","topics":[…]}
 *                      client → server  {"type":"sync_subscriptions","id":1}
 *                      server → client  {"type":"observed"}
 *                      server → client  {"type":"authority_context","workspaceCaps":[…],"workspaceEvents":true}
 *                      server → client  {"type":"subscriptions_synced","id":1}
 *                      server → client  {"type":"ping"}
 *                      server → client  {"type":"session","connectionId":"…"}
 *                      server → client  {"type":"plugins","roster":[…]}
 *                      server → client  {"type":"event","topic":{…},"plugin":"…","kind":"…",…}
 *   channel-level      both ways        {"ch":"<channelId>", "type":"…", …}
 *
 * A CHANNEL is one client-chosen handle onto one room. `ch` is opaque to the server,
 * unique per connection, and deliberately NOT a container id: two channels on one socket
 * may address the SAME container with different roles (an occupant's channel and a
 * portal's watching preview), so a container-keyed channel would be an id pun that
 * collides. `join` binds a fresh `ch` to a container, `leave` frees it, and every other
 * channel frame routes by it.
 * Liveness is a property of the socket, not of a room, so the ping/pong pair carries no
 * `ch`; neither does the plugin roster, which describes the whole workspace rather than
 * any one room, nor the event plane, whose topics are NODES and therefore routinely name
 * something no channel on this socket has joined (see CONNECTION_BODIES and
 * CLIENT_CONNECTION_BODIES).
 *
 * Handshake: the FIRST client frame on a connection MUST be `join` or `observe` (ten-second
 * deadline). `join` binds a room channel and earns `init`; `observe` authenticates a
 * connection that deliberately holds no room and earns `observed`. The accepted credential's
 * live `authority_context` arrives before either readiness frame. Per-channel epoch/rev
 * resume hints ride each channel's own `join`, so a reconnect redials ONE socket and
 * re-establishes every observer and channel.
 *
 * REFUSAL SCOPE (CONTRACTS-ready). A refusal closes the whole SOCKET when it invalidates
 * the credential or the framing itself: 4401 bad token · 4403 forbidden/revoked · 4409
 * protocol mismatch · 4002 malformed frame, non-handshake first frame, duplicate `observe`,
 * duplicate `ch`, or a connection holding neither an observer nor a channel. It closes ONE
 * CHANNEL — a `channel_closed` frame, socket untouched — when it concerns one room: 4404
 * unknown or deleted container · 4429 channel cap reached · 1009 that room's state exceeding
 * the transport ceiling · 1013 that channel's outbound queue overflowing. Killing a whole
 * tab because one portal pointed at a deleted container is precisely the blast radius
 * multiplexing exists to remove.
 */

/** Every base64 field on this wire, bounded once: doc updates and terminal bytes alike. */
const base64 = z.base64().max(MAX_SESSION_BASE64_CHARS);

/**
 * Channel ids are tokens, never arbitrary strings: both halves splice one into a frame
 * prefix (`{"ch":"c7",` + body) to tag a shared serialization for many peers, so an id
 * needing JSON escaping would be a correctness hole rather than a slow path.
 */
export const CHANNEL_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
export const ChannelIdSchema = z.string().regex(CHANNEL_ID_PATTERN);

/**
 * Server-side bound on channels per connection. A tab holds one channel per room it
 * renders (canvas + one per portal or tile), so the cap is generous for real
 * scenes while keeping a single socket's fan-out finite. Per-channel outbound queues are
 * unchanged by multiplexing, so this cap reproduces exactly the worst-case app-side
 * buffering that the same rooms held on separate sockets before v12.
 */
export const MAX_SESSION_CHANNELS_PER_CONNECTION = 64;

/** Channel-scoped close code for a connection that asked for one channel too many. */
export const CHANNEL_LIMIT_CLOSE_CODE = 4429;

/**
 * A live PTY as the wire describes it. Placement is deliberately ABSENT: a
 * terminal can be referenced from several places at once (portals on many canvases), so any
 * single `elementId` here would be a lie. Consumers read placement from live state — the
 * scene doc's elements and the container's layout tree.
 */
export const TerminalInfoSchema = z.strictObject({
  id: z.string().min(1),
  /**
   * The composition this terminal LIVES IN — never a canvas, and never null. A terminal is
   * `homed: "eager"`: its composition is born with it and outlives every reference to it,
   * so "unbound" is not a state a terminal can be in. It is also the room this terminal's
   * frames travel through, which is why a canvas showing the terminal through a portal
   * joins that room rather than streaming the terminal over its own.
   */
  containerId: z.string().min(1),
  /** Operator-assigned display name; null means the client renders its default label. */
  name: z.string().min(1).max(120).nullable(),
  machineId: z.string().min(1),
  session: SessionRefSchema.optional(),
  status: z.enum(["running", "exited"]),
  exitCode: z.number().int().nullable(),
  /**
   * Why the terminal's owner ended it, or null for a running terminal and an ordinary exit of
   * its own program (issue #853). It resets to null when the terminal restarts.
   */
  exitReason: TerminalExitReasonSchema.nullable(),
  /** Launch intent may be relative until the owner reports an observed absolute directory. */
  cwd: TerminalCwdSchema.optional(),
  /**
   * The first application-owned readiness observation for this PTY, or null when no truthful
   * observation exists. It resets to null when the terminal restarts.
   */
  readiness: TerminalReadinessSchema.nullable(),
  cols: z.number().int().positive().max(1000),
  rows: z.number().int().positive().max(1000),
  controllerId: z.string().nullable(),
  createdBy: z.string().min(1),
});
export type TerminalInfo = z.infer<typeof TerminalInfoSchema>;

export const ErrorCodeSchema = z.enum([
  "unauthorized",
  "forbidden",
  "not_found",
  "invalid",
  "conflict",
  "no_machine",
  "not_controller",
  "epoch_mismatch",
  "rate_limited",
  /**
   * The request is well-formed and permitted, but the peer it targets predates a field it
   * carries: today, `terminal_open.program` aimed at a machine whose agent spoke a protocol
   * older than v22. The remedy is a different machine (or an upgraded agent), which neither
   * `no_machine` (offline or ambiguous) nor `conflict` (state) would say.
   */
  "unsupported",
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/**
 * Bounds for the environment an opener may hand a PTY (`terminal_open.env`): a handful of
 * mode flags and paths, never a payload channel — the shell is the payload channel.
 */
export const MAX_TERMINAL_ENV_KEYS = 32;
export const MAX_TERMINAL_ENV_VALUE_CHARS = 4096;
/** POSIX portable-name keys, upper case, and NEVER the server's own prefix. */
export const TERMINAL_ENV_KEY_PATTERN = /^[A-Z_][A-Z0-9_]*$/;
export const TERMINAL_ENV_RESERVED_PREFIX = "MANIFOLD_";

/**
 * Environment an opener adds to the PTY it births, merged UNDER the four fixed `MANIFOLD_*`
 * keys the server mints (CONTRACTS.md §terminals over the session channel). The reserved
 * prefix is refused by SHAPE rather than by merge order alone, so a plugin that tries to
 * forge `MANIFOLD_TOKEN` is told `invalid` at the frame instead of silently losing.
 */
export const TerminalEnvSchema = z
  .record(
    z
      .string()
      .regex(TERMINAL_ENV_KEY_PATTERN, "env keys are upper-case POSIX names")
      .refine((key) => !key.startsWith(TERMINAL_ENV_RESERVED_PREFIX), {
        message: `env keys may not start with ${TERMINAL_ENV_RESERVED_PREFIX}`,
      }),
    z.string().max(MAX_TERMINAL_ENV_VALUE_CHARS),
  )
  .refine((env) => Object.keys(env).length <= MAX_TERMINAL_ENV_KEYS, {
    message: `at most ${MAX_TERMINAL_ENV_KEYS} env keys`,
  });
export type TerminalEnv = z.infer<typeof TerminalEnvSchema>;

const terminalGeometry = {
  cols: z.number().int().positive().max(1000),
  rows: z.number().int().positive().max(1000),
};

/** Foreground measurements expire even when a suspended client cannot withdraw them. */
export const TERMINAL_VIEWPORT_REFRESH_MS = 10_000;
export const TERMINAL_VIEWPORT_LEASE_MS = 30_000;
export const MAX_TERMINAL_VIEWPORTS = 64;
/**
 * One mounted view's opaque, client-chosen identity on its channel. It names both that view's
 * terminal delivery (attach/detach/ack and every stream frame) and its desired-geometry lease.
 */
export const TerminalViewportIdSchema = z.string().min(1).max(128);
/**
 * A server-minted delivery incarnation of one view. Every `terminal_snapshot` starts a fresh
 * one; a client only echoes it back, so a stale incarnation can never be credited.
 */
export const TerminalDeliveryIdSchema = z.string().min(1).max(128);
/**
 * One view's frame ordinal inside its incarnation: the snapshot is 0, then every output and
 * geometry frame sent to that view takes the next integer in source arrival order. It is
 * independent of the PTY byte `seq` and of the geometry revision.
 */
export const TerminalDeliverySeqSchema = z.number().int().nonnegative();
/**
 * Transient delivery state of one view (issue #880), never recording metadata. `waiting`: its
 * completed-parse credit is exhausted and the server holds its ordered pending frames.
 * `recovering`: a pending window overflowed or the owner was re-adopted while work remained.
 * Once every sent frame completes, a fresh snapshot replaces this incarnation. `skipped`
 * states whether any held or newer output was omitted; recovery alone does not imply loss.
 * `live`: held frames drained again. A snapshot itself starts a live incarnation. `refused`:
 * the server retired this attachment of the view, and only a deliberate re-attach continues it.
 */
export const TerminalDeliveryStateSchema = z.enum(["waiting", "recovering", "live", "refused"]);
export type TerminalDeliveryState = z.infer<typeof TerminalDeliveryStateSchema>;
/**
 * Why a view's attachment was retired. `not_found`/`exited`/`view_limit` refuse the attach
 * itself; `exited` with `skipped: true` also ends a live view whose held output the stream's
 * end discarded, sent before the exit; `owner_unavailable` means its snapshot request could
 * not reach the terminal's owner;
 * `snapshot_timeout` that the owner did not answer within the finite snapshot deadline;
 * `pending_overflow` that output outgrew the pending bound before the snapshot arrived, so no
 * tail could follow it without a gap. None of these is retried by the server.
 */
export const TerminalDeliveryRefusalSchema = z.enum([
  "not_found",
  "exited",
  "view_limit",
  "owner_unavailable",
  "snapshot_timeout",
  "pending_overflow",
]);
export type TerminalDeliveryRefusal = z.infer<typeof TerminalDeliveryRefusalSchema>;

/**
 * Per-view parser-credit bounds (issue #880). The byte unit is {@link terminalDeliveryCharge}:
 * the encoded ASCII base64 characters of a frame's `data`, i.e. its payload bytes on the wire
 * and an upper bound on the decoded bytes a parser consumes. A geometry frame charges no payload
 * bytes. Envelope metadata is bounded separately by the frame counts, so none of these claims an
 * exact aggregate of serialized frame memory. Each window admits any single frame, because
 * `MAX_SESSION_BASE64_CHARS` is below every byte bound.
 *
 * UNACKED: frames sent to one view's current incarnation and not yet acknowledged as completed.
 * PENDING: ordered frames the server holds for one view, awaiting its snapshot or its credit.
 * PARSER: a client's outstanding parser work for one view, active plus queued.
 */
export const MAX_TERMINAL_DELIVERY_UNACKED_BYTES = 1_048_576;
export const MAX_TERMINAL_DELIVERY_UNACKED_FRAMES = 256;
export const MAX_TERMINAL_DELIVERY_PENDING_BYTES = 1_048_576;
export const MAX_TERMINAL_DELIVERY_PENDING_FRAMES = 256;
export const MAX_TERMINAL_PARSER_BYTES = 1_048_576;
export const MAX_TERMINAL_PARSER_FRAMES = 256;

/**
 * The one charge unit every terminal delivery bound counts: encoded payload characters. It
 * takes any delivery frame, stamped for a view or not; a geometry frame has no payload.
 */
export function terminalDeliveryCharge(
  frame: Pick<TerminalDeliveryFrame, "type"> & { readonly data?: string },
): number {
  return frame.data?.length ?? 0;
}

const TerminalViewportRefSchema = z.strictObject({
  connId: z.string().min(1).max(128),
  viewportId: TerminalViewportIdSchema,
});
/** Ephemeral attribution only; names come from the recipient's existing visible presence. */
export const TerminalSizingSchema = z.strictObject({
  mode: z.enum(["smallest", "retained"]),
  columns: z.array(TerminalViewportRefSchema).max(MAX_TERMINAL_VIEWPORTS),
  rows: z.array(TerminalViewportRefSchema).max(MAX_TERMINAL_VIEWPORTS),
});
export type TerminalSizing = z.infer<typeof TerminalSizingSchema>;
const optionalTerminalGeometry = {
  cols: terminalGeometry.cols.optional(),
  rows: terminalGeometry.rows.optional(),
};
export const GestureFields = {
  kind: GestureKindSchema,
  phase: z.enum(["active", "end"]),
  elementId: z.string().min(1).max(128),
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().positive().optional(),
  height: z.number().finite().positive().optional(),
  points: z.array(z.number().finite()).max(MAX_GESTURE_POINT_VALUES).optional(),
  /**
   * Set on `carry` frames only: the item in flight, so a viewer can render what is
   * being moved without resolving ids it may have no room for. Geometry above then
   * says WHERE that item's representation currently is.
   */
  carry: CarrySchema.optional(),
};
export const GestureSchema = z.strictObject(GestureFields);
export type Gesture = z.infer<typeof GestureSchema>;

/**
 * Adds the routing field to one frame body. Each variant is channelized by an explicit
 * call so the discriminated unions below stay fully inferred — a body union and a wire
 * union built from the SAME shapes, never two hand-maintained copies.
 */
function channelized<T extends z.ZodObject>(
  body: T,
): z.ZodObject<T["shape"] & { ch: typeof ChannelIdSchema }> {
  return body.extend({ ch: ChannelIdSchema });
}

// ---------------------------------------------------------------------------- client → server

const CLIENT_BODIES = {
  join: z.strictObject({
    type: z.literal("join"),
    containerId: z.string().min(1),
    token: z.string().min(1),
    protocolVersion: z.number().int().positive(),
    /**
     * A spectator watches without occupying: a portal's live preview joins a real
     * room channel, and counting it as an occupant would both fake an avatar and pin a
     * bubble open forever. Spectators receive state and terminal output but may never
     * write, and they appear in neither the attendance frame nor `/api/attendance`. Absent ≡
     * occupant. A role change is a `leave`+`join` on the same socket, never TCP churn.
     */
    spectator: z.boolean().optional(),
    /** Resume hints for THIS channel; a mismatch simply yields a full init. */
    lastEpoch: z.string().optional(),
    lastRev: z.number().int().nonnegative().optional(),
  }),
  /**
   * Frees one channel: the room loses this peer (attendance, presence, terminal viewers)
   * while every other channel on the socket keeps streaming. A client that is closing
   * its LAST channel closes the socket instead — the close already means "leave
   * everything", so a redundant frame would be pure ceremony.
   */
  leave: z.strictObject({ type: z.literal("leave") }),
  doc_update: z.strictObject({
    type: z.literal("doc_update"),
    update: base64,
  }),
  gesture: z.strictObject({ type: z.literal("gesture"), ...GestureFields }),
  presence: z.strictObject({ type: z.literal("presence"), payload: PresencePayloadSchema }),
  cursor: z.strictObject({
    type: z.literal("cursor"),
    x: z.number().finite(),
    y: z.number().finite(),
  }),
  resync_request: z.strictObject({ type: z.literal("resync_request") }),
  terminal_open: z.strictObject({
    type: z.literal("terminal_open"),
    /**
     * Correlation token, and under the default element placement also the id of the
     * canvas element the opener authors once the PTY lands. A composition's opener authors
     * nothing, so there this is a pure ref, echoed back as `terminal_opened.ref`.
     */
    elementId: z.string().min(1),
    ...optionalTerminalGeometry,
    cwd: TerminalCwdSchema.optional(),
    machineId: z.string().optional(),
    /**
     * `"tile"` hands placement to the container: a composition has no canvas to author into,
     * so the server writes the tile leaf itself and answers with that tile id as the
     * placement `elementId`. Absent ≡ the opener places a canvas element, so every
     * pre-flag client keeps its exact semantics.
     */
    placement: z.literal("tile").optional(),
    /**
     * The program the PTY execs instead of the machine's shell (issue #192). The gateway
     * hands it, with `env`, to `core.terminals.open` before any create — the door judges
     * what will run and its trace records it — and only then carries it to the agent as
     * `create.program`, refused `unsupported` when the target machine's agent predates the
     * field. Absent ≡ the login shell, exactly the pre-v22 gesture.
     */
    program: TerminalProgramSchema.optional(),
    runtime: TerminalRuntimeSchema.optional(),
    /** Environment merged under the fixed `MANIFOLD_*` keys; absent ≡ nothing added. */
    env: TerminalEnvSchema.optional(),
  }),
  /**
   * Starts one view's delivery: a fresh snapshot incarnation, then its ordered tail. Re-attaching
   * the same `(terminalId, viewportId)` retires that view's prior incarnation and its credit.
   */
  terminal_attach: z.strictObject({
    type: z.literal("terminal_attach"),
    terminalId: z.string().min(1),
    viewportId: TerminalViewportIdSchema,
  }),
  /** Releases exactly one view; sibling views of the terminal on this channel keep streaming. */
  terminal_detach: z.strictObject({
    type: z.literal("terminal_detach"),
    terminalId: z.string().min(1),
    viewportId: TerminalViewportIdSchema,
  }),
  /**
   * Cumulative completion credit: this view's parser has FINISHED every frame of `deliveryId`
   * up to and including `deliverySeq`. Receipt on the socket is not completion. A stale
   * incarnation, a duplicate or an ordinal never sent grants nothing.
   */
  terminal_ack: z.strictObject({
    type: z.literal("terminal_ack"),
    terminalId: z.string().min(1),
    viewportId: TerminalViewportIdSchema,
    deliveryId: TerminalDeliveryIdSchema,
    deliverySeq: TerminalDeliverySeqSchema,
  }),
  terminal_input: z.strictObject({
    type: z.literal("terminal_input"),
    terminalId: z.string().min(1),
    data: base64,
  }),
  terminal_resize: z.strictObject({
    type: z.literal("terminal_resize"),
    terminalId: z.string().min(1),
    /** The attached view whose desired geometry this is, on this channel. */
    viewportId: TerminalViewportIdSchema,
    /** null withdraws this view; desired dimensions are independent of the applied grid. */
    viewport: z.strictObject(terminalGeometry).nullable(),
  }),
  terminal_take: z.strictObject({
    type: z.literal("terminal_take"),
    terminalId: z.string().min(1),
  }),
  terminal_kill: z.strictObject({
    type: z.literal("terminal_kill"),
    terminalId: z.string().min(1),
  }),
} as const;

/**
 * Connection-level: the answer to the server's liveness ping. Liveness belongs to the
 * socket, so it carries no channel — and the client ANSWERS rather than asks, because a
 * background tab's timers are throttled while its message handler is not (see
 * `DIAL_PING_INTERVAL_MS`).
 */
const ClientPongSchema = z.strictObject({ type: z.literal("pong") });

/** Correlations are bounded to a positive signed 32-bit integer on both sides of the fence. */
const SubscriptionSyncIdSchema = z.number().int().positive().max(2_147_483_647);

/**
 * CONNECTION-LEVEL client frames with a payload: the subscription pair (ADR 0012).
 *
 * They carry no `ch` because a TOPIC IS A NODE, and a node is not a room: a client watching
 * the machine roster or a terminal in a container it never joined has no channel to tag, and
 * tagging one would tie a subscription's lifetime to a room membership that has nothing to do
 * with it. Subscriptions are presence-class state — they live on the socket, die with it, and
 * are never persisted.
 *
 * A topic travels STRUCTURED, never as a `manifold://` string, and that is a mechanism rather
 * than a preference: a topic namespace joined at runtime would need its own registry
 * (`REGISTRY.md` §Runtime-joined namespaces), and a wire that cannot carry a hand-typed topic
 * has no such namespace to police. `formatManifoldUri` remains the one joiner, used by each
 * side to key its own index.
 *
 * There is NO per-topic acknowledgement. A subscribe is a declaration of interest, exactly
 * as a presence frame is: the server subscribes the topics this credential may read and
 * silently declines the rest, because a per-topic refusal would turn the event plane into an
 * oracle answering "does this node exist and may I read it?" one probe at a time.
 *
 * `sync_subscriptions` is only a transport ordering fence. Its ID-only reply follows all
 * preceding subscription declarations on this authenticated socket, identically whether they
 * were accepted or refused; it neither acknowledges delivery nor waits for unrelated effects.
 * Workspace event eligibility comes from this credential's own `authority_context`, never
 * from the fence or a mounted room's caps. Catch-up reads happen after the fence.
 *
 * Ordering is the socket's: the credential arrives on `join` or `observe`, so a
 * `subscribe` before the first handshake has nothing to authorize against and is refused
 * with the frame grammar's existing 4002. A roomless observer may hold subscriptions.
 */
export const CLIENT_CONNECTION_BODIES = {
  /** Authenticates a connection that needs workspace frames and event subscriptions, but no room. */
  observe: z.strictObject({
    type: z.literal("observe"),
    token: z.string().min(1),
    protocolVersion: z.number().int().positive(),
  }),
  ...STREAM_CLIENT_BODIES,
  subscribe: z.strictObject({
    type: z.literal("subscribe"),
    topics: z.array(ManifoldRefSchema).min(1).max(MAX_SUBSCRIBE_TOPICS),
  }),
  unsubscribe: z.strictObject({
    type: z.literal("unsubscribe"),
    topics: z.array(ManifoldRefSchema).min(1).max(MAX_SUBSCRIBE_TOPICS),
  }),
  sync_subscriptions: z.strictObject({
    type: z.literal("sync_subscriptions"),
    id: SubscriptionSyncIdSchema,
  }),
} as const;

/**
 * One frame's payload, independent of routing. The server constructs and validates
 * bodies (a broadcast serializes ONE body and tags it per peer), and an SDK channel
 * handle hands bodies to its subscribers — the channel is implicit in the handle.
 */
export const ClientMessageBodySchema = z.discriminatedUnion("type", [
  CLIENT_BODIES.join,
  CLIENT_BODIES.leave,
  CLIENT_BODIES.doc_update,
  CLIENT_BODIES.gesture,
  CLIENT_BODIES.presence,
  CLIENT_BODIES.cursor,
  CLIENT_BODIES.resync_request,
  CLIENT_BODIES.terminal_open,
  CLIENT_BODIES.terminal_attach,
  CLIENT_BODIES.terminal_detach,
  CLIENT_BODIES.terminal_ack,
  CLIENT_BODIES.terminal_input,
  CLIENT_BODIES.terminal_resize,
  CLIENT_BODIES.terminal_take,
  CLIENT_BODIES.terminal_kill,
  CLIENT_CONNECTION_BODIES.observe,
  ClientPongSchema,
  CLIENT_CONNECTION_BODIES.subscribe,
  CLIENT_CONNECTION_BODIES.unsubscribe,
  CLIENT_CONNECTION_BODIES.sync_subscriptions,
  CLIENT_CONNECTION_BODIES.stream_open,
  CLIENT_CONNECTION_BODIES.stream_close,
]);
export type ClientMessageBody = z.infer<typeof ClientMessageBodySchema>;

/** The wire frame: a body plus its routing field, exactly as it appears on the socket. */
export const ClientMessageSchema = z.discriminatedUnion("type", [
  channelized(CLIENT_BODIES.join),
  channelized(CLIENT_BODIES.leave),
  channelized(CLIENT_BODIES.doc_update),
  channelized(CLIENT_BODIES.gesture),
  channelized(CLIENT_BODIES.presence),
  channelized(CLIENT_BODIES.cursor),
  channelized(CLIENT_BODIES.resync_request),
  channelized(CLIENT_BODIES.terminal_open),
  channelized(CLIENT_BODIES.terminal_attach),
  channelized(CLIENT_BODIES.terminal_detach),
  channelized(CLIENT_BODIES.terminal_ack),
  channelized(CLIENT_BODIES.terminal_input),
  channelized(CLIENT_BODIES.terminal_resize),
  channelized(CLIENT_BODIES.terminal_take),
  channelized(CLIENT_BODIES.terminal_kill),
  CLIENT_CONNECTION_BODIES.observe,
  ClientPongSchema,
  // Connection-level: identical in both unions, because a frame with no `ch` IS its body.
  CLIENT_CONNECTION_BODIES.subscribe,
  CLIENT_CONNECTION_BODIES.unsubscribe,
  CLIENT_CONNECTION_BODIES.sync_subscriptions,
  CLIENT_CONNECTION_BODIES.stream_open,
  CLIENT_CONNECTION_BODIES.stream_close,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ---------------------------------------------------------------------------- server → client

/** Full-state payload shared by `init` and `resync`. */
const stateFields = {
  protocolVersion: z.number().int().positive(),
  epoch: z.string().min(1),
  rev: z.number().int().nonnegative(),
  doc: z.base64(),
  self: PrincipalSchema,
  /** The joining principal's granted capabilities; drives client-side affordances. */
  selfCaps: z.array(CapSchema).min(1),
  /**
   * Server-assigned identity for this CHANNEL; changes on every join. Two channels of
   * one tab are two room memberships, exactly as two sockets were before v12, so
   * attendance, cursor echo-suppression, and presence keying are untouched.
   */
  selfConnId: z.string().min(1),
  /** Who is in this room right now, one row per attending principal. */
  attendance: z.array(PresenceStateSchema),
  terminals: z.array(TerminalInfoSchema),
};

/** Null marks retained legacy owners whose resize has no owner-relative stream boundary. */
const ViewerTerminalGeometrySchema = TerminalGeometrySchema.extend({
  revision: z.number().int().nonnegative().nullable(),
});

/**
 * Which view a stream frame belongs to and its place in that view's current incarnation. Two
 * views of one terminal on one channel are two independent deliveries, so a reader filters by
 * `terminalId` AND `viewportId`, and acknowledges by `deliveryId` and `deliverySeq`.
 */
const terminalDeliveryFields = {
  viewportId: TerminalViewportIdSchema,
  deliveryId: TerminalDeliveryIdSchema,
  deliverySeq: TerminalDeliverySeqSchema,
};

const SERVER_BODIES = {
  init: z.strictObject({ type: z.literal("init"), ...stateFields }),
  resync: z.strictObject({ type: z.literal("resync"), ...stateFields }),
  doc_update: z.strictObject({
    type: z.literal("doc_update"),
    update: base64,
    by: z.string().min(1),
  }),
  gesture: z.strictObject({
    type: z.literal("gesture"),
    principalId: z.string().min(1),
    connId: z.string().min(1),
    /**
     * Set ONLY on the copy the server fans into the room a carry's aim ADDRESSES, which is
     * not the room the gesture is happening in: a tile dragged over a portal streams in the
     * CANVAS's room while the split it previews lands in the portal's container, and a
     * collaborator sitting in that container's own view is in neither the sender's room nor
     * reach of its frames.
     *
     * Its geometry is in the ORIGIN room's space and means nothing here, so a receiver reads
     * the aim and paints no ghost from it. Server-stamped and server-only: the client body
     * above has no such field, so a peer cannot forge one. Absent — every ordinary frame — is
     * a gesture happening in this room, unchanged.
     */
    aimOnly: z.literal(true).optional(),
    ...GestureFields,
  }),
  /** One principal joined or left this room: the attendance delta. */
  attendance: z.strictObject({
    type: z.literal("attendance"),
    joined: PresenceStateSchema.optional(),
    left: z.strictObject({ principalId: z.string().min(1) }).optional(),
  }),
  presence: z.strictObject({
    type: z.literal("presence"),
    principalId: z.string().min(1),
    connId: z.string().min(1),
    payload: PresencePayloadSchema,
  }),
  cursor: z.strictObject({
    type: z.literal("cursor"),
    principalId: z.string().min(1),
    connId: z.string().min(1),
    x: z.number().finite(),
    y: z.number().finite(),
  }),
  terminal_opened: z.strictObject({
    type: z.literal("terminal_opened"),
    /** The placement: a canvas element id, or the tile id of a server-authored leaf. */
    elementId: z.string().min(1),
    terminal: TerminalInfoSchema,
    /**
     * Echoes `terminal_open.elementId` when the SERVER authored the placement, so the
     * opener can correlate a reply whose `elementId` it never chose. Sent ONLY to an
     * opener that asked for `placement: "tile"`: peers strict-parse this union, so an
     * unsolicited extra key would break their socket.
     */
    ref: z.string().min(1).optional(),
  }),
  terminal_snapshot: z.strictObject({
    type: z.literal("terminal_snapshot"),
    terminalId: z.string().min(1),
    ...terminalDeliveryFields,
    /** A snapshot starts a fresh incarnation at ordinal 0. */
    deliverySeq: z.literal(0),
    /** Byte-sequence watermark: outputs with seq > this follow with no gap. */
    seq: z.number().int().nonnegative(),
    data: base64,
    geometry: ViewerTerminalGeometrySchema,
    /**
     * True when this view's server-held output was discarded before this snapshot replaced it:
     * output and history may be missing from the view. It names no byte count and promises no
     * recording; false only says this recovery discarded nothing for the view.
     */
    skipped: z.boolean(),
  }),
  terminal_output: z.strictObject({
    type: z.literal("terminal_output"),
    terminalId: z.string().min(1),
    ...terminalDeliveryFields,
    seq: z.number().int().positive(),
    data: base64,
  }),
  terminal_geometry: z.strictObject({
    type: z.literal("terminal_geometry"),
    terminalId: z.string().min(1),
    ...terminalDeliveryFields,
    seq: z.number().int().nonnegative(),
    geometry: ViewerTerminalGeometrySchema,
  }),
  /**
   * One view's delivery-state TRANSITION, sent once per change and never repeated while the
   * state holds. It is not a stream frame: no ordinal, no charge, never acknowledged.
   * `skipped` is true only when this view's held output was discarded. `deliveryId` is null
   * only on a refusal of a view that never received a snapshot in this attachment; `reason` is
   * set exactly on `refused`.
   */
  terminal_delivery: z.strictObject({
    type: z.literal("terminal_delivery"),
    terminalId: z.string().min(1),
    viewportId: TerminalViewportIdSchema,
    deliveryId: TerminalDeliveryIdSchema.nullable(),
    state: TerminalDeliveryStateSchema,
    skipped: z.boolean(),
    reason: TerminalDeliveryRefusalSchema.nullable(),
  }),
  terminal_event: z.strictObject({
    type: z.literal("terminal_event"),
    terminalId: z.string().min(1),
    kind: z.enum([
      "opened",
      "exited",
      "controller_changed",
      "resized",
      "parked",
      "renamed",
      "cwd",
      "restarted",
      "ready",
    ]),
    exitCode: z.number().int().nullable().optional(),
    /** On `exited` only, and only when the terminal's owner ended it; absent otherwise. */
    exitReason: TerminalExitReasonSchema.optional(),
    controllerId: z.string().nullable().optional(),
    cols: z.number().int().positive().optional(),
    rows: z.number().int().positive().optional(),
    name: z.string().min(1).max(120).optional(),
    cwd: MachinePathSchema.optional(),
    fallback: z.enum(["original", "home", "no_recipe"]).optional(),
    readiness: TerminalReadinessSchema.optional(),
  }),
  terminal_sizing: z.strictObject({
    type: z.literal("terminal_sizing"),
    terminalId: z.string().min(1),
    sizing: TerminalSizingSchema,
  }),
  saved: z.strictObject({
    type: z.literal("saved"),
    rev: z.number().int().nonnegative(),
    at: z.number().int().nonnegative(),
  }),
  error: z.strictObject({
    type: z.literal("error"),
    code: ErrorCodeSchema,
    message: z.string().optional(),
    /** Correlates to updateId / terminalId when applicable. */
    ref: z.string().optional(),
  }),
  /**
   * This channel is over, the socket is not. It carries the same close-code vocabulary a
   * socket close would (4404 container gone, 4429 channel cap, 1009/1013 transport bounds), so
   * a client reports and heals a dead room exactly as it did when a room WAS a socket.
   */
  channel_closed: z.strictObject({
    type: z.literal("channel_closed"),
    code: z.number().int().positive(),
    reason: z.string(),
  }),
} as const;

/** Connection-level: the liveness probe every dialed-in side sends on its own cadence. */
const ServerPingSchema = z.strictObject({ type: z.literal("ping") });

/**
 * CONNECTION-LEVEL server frames: they address the SOCKET, not a channel, so they carry no
 * `ch` at all. Until v14 the only such frames were the liveness pair, whose bodies are a
 * bare type; the roster was the first category with a payload, and it exists because plugin
 * REGISTRATION is workspace-global — the roster is not a property of any room, so tagging
 * it with one room's channel would be an id pun and fanning it out per channel would send
 * one fact N times.
 *
 * A roster frame is delivered on socket open (before any `join`) and again on every change,
 * which is what lets a client rebuild its assembly live instead of reloading.
 *
 * An `event` frame is the same category for the same reason: its topic is a NODE, which may
 * be a container nobody on this socket joined, a principal, or a plugin's own node. It is
 * delivered to the sockets subscribed AT THE INSTANT OF EMISSION and to no others — there is
 * no offset, no delivery acknowledgement and no replay, because catch-up is reading state
 * back through the door after a transport subscription fence (ADR 0012 §5).
 *
 * Kept as a keyed table so routing can look a frame's parser up by type. `ping` stays a
 * bare literal beside it rather than joining the table: it has no body to parse.
 */
export const CONNECTION_BODIES = {
  /** Acknowledges a roomless observer after its credential and protocol version are accepted. */
  observed: z.strictObject({ type: z.literal("observed") }),
  /**
   * This physical credential's live, coarse workspace authority, before readiness and on
   * change. It is neither a per-node subscription verdict nor authorization for an action.
   */
  authority_context: z.strictObject({
    type: z.literal("authority_context"),
    workspaceCaps: z.array(CapSchema),
    workspaceEvents: z.boolean(),
  }),
  /** Ordering only: no topic, count, existence, delivery or authorization information. */
  subscriptions_synced: z.strictObject({
    type: z.literal("subscriptions_synced"),
    id: SubscriptionSyncIdSchema,
  }),
  /**
   * Server-issued correlation for this physical socket. It arrives before any room join,
   * so a refusal before `init` still has an identifier the browser can show an operator.
   * It is diagnostic only: never authority, identity, or a persistent device fingerprint.
   */
  session: z.strictObject({
    type: z.literal("session"),
    connectionId: z.string().min(1).max(128),
  }),
  ...STREAM_SERVER_BODIES,
  /** The roster and, beside it, the developer-mode switch (`PluginsResponseSchema`); absent ≡ off. */
  plugins: z.strictObject({
    type: z.literal("plugins"),
    roster: PluginRosterSchema,
    developerMode: z.boolean().optional(),
  }),
  /**
   * ONE thing happened to ONE node, emitted at the door that committed it — once, at the
   * commit point, never per frame of the gesture that led there.
   *
   * `topic` is the node it happened to, `plugin` is the originating plugin, and `kind` is what
   * happened within that plugin's declared vocabulary. Subscriptions match only topic nodes;
   * consumers matching a kind also match its plugin. The two `kind` words in this frame are
   * one canon word at two depths — `topic.kind` discriminates the ADDRESS FORM (the closed
   * union in `uri.ts`), `kind` names the event class exactly as `terminal_event.kind` does
   * for a PTY.
   *
   * `actor` is the principal whose action committed the change, or null when the engine acted
   * with no principal behind it (an agent socket dying, a migration, a timer). It is stated
   * rather than implied so a subscriber can suppress the echo of its own mutation without
   * correlating anything.
   *
   * `payload` is REQUIRED and may be empty. An absent-versus-empty distinction on a
   * notification would be a fact nobody can act on, and every consumer would pay for the
   * branch; the emitter sends `{}` and the reader indexes.
   */
  event: z.strictObject({
    type: z.literal("event"),
    topic: ManifoldRefSchema,
    plugin: PluginIdSchema,
    kind: EventKindSchema,
    /** Server clock, ms since epoch — the same stamp `saved.at` carries. */
    at: z.number().int().nonnegative(),
    actor: z.string().min(1).nullable(),
    payload: EventPayloadSchema,
  }),
} as const;

export const ServerMessageBodySchema = z.discriminatedUnion("type", [
  SERVER_BODIES.init,
  SERVER_BODIES.resync,
  SERVER_BODIES.doc_update,
  SERVER_BODIES.gesture,
  SERVER_BODIES.attendance,
  SERVER_BODIES.presence,
  SERVER_BODIES.cursor,
  SERVER_BODIES.terminal_opened,
  SERVER_BODIES.terminal_snapshot,
  SERVER_BODIES.terminal_output,
  SERVER_BODIES.terminal_geometry,
  SERVER_BODIES.terminal_delivery,
  SERVER_BODIES.terminal_event,
  SERVER_BODIES.terminal_sizing,
  SERVER_BODIES.saved,
  SERVER_BODIES.error,
  SERVER_BODIES.channel_closed,
  CONNECTION_BODIES.observed,
  CONNECTION_BODIES.authority_context,
  CONNECTION_BODIES.subscriptions_synced,
  ServerPingSchema,
  CONNECTION_BODIES.session,
  CONNECTION_BODIES.plugins,
  CONNECTION_BODIES.event,
  CONNECTION_BODIES.stream_snapshot,
  CONNECTION_BODIES.stream_frame,
  CONNECTION_BODIES.stream_gap,
  CONNECTION_BODIES.stream_reset,
  CONNECTION_BODIES.stream_refused,
  CONNECTION_BODIES.stream_closed,
]);
export type ServerMessageBody = z.infer<typeof ServerMessageBodySchema>;

export const ServerMessageSchema = z.discriminatedUnion("type", [
  channelized(SERVER_BODIES.init),
  channelized(SERVER_BODIES.resync),
  channelized(SERVER_BODIES.doc_update),
  channelized(SERVER_BODIES.gesture),
  channelized(SERVER_BODIES.attendance),
  channelized(SERVER_BODIES.presence),
  channelized(SERVER_BODIES.cursor),
  channelized(SERVER_BODIES.terminal_opened),
  channelized(SERVER_BODIES.terminal_snapshot),
  channelized(SERVER_BODIES.terminal_output),
  channelized(SERVER_BODIES.terminal_geometry),
  channelized(SERVER_BODIES.terminal_delivery),
  channelized(SERVER_BODIES.terminal_event),
  channelized(SERVER_BODIES.terminal_sizing),
  channelized(SERVER_BODIES.saved),
  channelized(SERVER_BODIES.error),
  channelized(SERVER_BODIES.channel_closed),
  CONNECTION_BODIES.observed,
  CONNECTION_BODIES.authority_context,
  CONNECTION_BODIES.subscriptions_synced,
  ServerPingSchema,
  // Connection-level: identical in both unions, because a frame with no `ch` IS its body.
  CONNECTION_BODIES.session,
  CONNECTION_BODIES.plugins,
  CONNECTION_BODIES.event,
  CONNECTION_BODIES.stream_snapshot,
  CONNECTION_BODIES.stream_frame,
  CONNECTION_BODIES.stream_gap,
  CONNECTION_BODIES.stream_reset,
  CONNECTION_BODIES.stream_refused,
  CONNECTION_BODIES.stream_closed,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/**
 * Gesture frames as CONSUMERS see them: channel-agnostic. A wire frame satisfies this
 * type structurally (its `ch` is one extra key), so remote-gesture projection code is
 * written once against the body and works for any channel.
 */
export type ServerGesture = Extract<ServerMessageBody, { type: "gesture" }>;

/**
 * Event frames as CONSUMERS see them, the same way {@link ServerGesture} is: an SDK hands its
 * subscribers this body, and a subscriber matches it by formatting `topic` through
 * `formatManifoldUri` — never by comparing address strings it built itself.
 */
export type ServerEvent = Extract<ServerMessageBody, { type: "event" }>;

/** A view's parser-credited stream frames, as consumers see them. */
export type TerminalDeliveryFrame = Extract<
  ServerMessageBody,
  { type: "terminal_snapshot" | "terminal_output" | "terminal_geometry" }
>;

// ---------------------------------------------------------------------------- type inventories

/**
 * Public literal inventories for frame classification (unknown-type = forward-compat
 * ignore vs malformed-known-type = protocol error). Kept as plain constants so no
 * consumer ever reaches into zod internals; exhaustiveness is enforced at compile time
 * in both directions.
 */
export const SERVER_MESSAGE_TYPES = [
  "observed",
  "authority_context",
  "subscriptions_synced",
  "init",
  "resync",
  "doc_update",
  "gesture",
  "attendance",
  "presence",
  "cursor",
  "terminal_opened",
  "terminal_snapshot",
  "terminal_output",
  "terminal_geometry",
  "terminal_delivery",
  "terminal_event",
  "terminal_sizing",
  "saved",
  "error",
  "channel_closed",
  "ping",
  "session",
  "plugins",
  "event",
  "stream_snapshot",
  "stream_frame",
  "stream_gap",
  "stream_reset",
  "stream_refused",
  "stream_closed",
] as const satisfies readonly ServerMessage["type"][];

export const CLIENT_MESSAGE_TYPES = [
  "observe",
  "join",
  "leave",
  "doc_update",
  "gesture",
  "presence",
  "cursor",
  "resync_request",
  "terminal_open",
  "terminal_attach",
  "terminal_detach",
  "terminal_ack",
  "terminal_input",
  "terminal_resize",
  "terminal_take",
  "terminal_kill",
  "pong",
  "subscribe",
  "unsubscribe",
  "sync_subscriptions",
  "stream_open",
  "stream_close",
] as const satisfies readonly ClientMessage["type"][];

/**
 * Frames that carry no `ch`: the socket's handshake/liveness pair, plus every frame that
 * describes the CONNECTION's world rather than a room's — the plugin roster, live authority,
 * and the event plane's declarations, ordering fence and notification. Routing uses this to
 * distinguish connection-level and channel-level frames, and a channel handle is never
 * handed one of these.
 */
export const CONNECTION_LEVEL_MESSAGE_TYPES = [
  "observe",
  "observed",
  "authority_context",
  "sync_subscriptions",
  "subscriptions_synced",
  "ping",
  "pong",
  "session",
  "plugins",
  "subscribe",
  "unsubscribe",
  "event",
  "stream_open",
  "stream_close",
  "stream_snapshot",
  "stream_frame",
  "stream_gap",
  "stream_reset",
  "stream_refused",
  "stream_closed",
] as const;

type MissingServerType = Exclude<ServerMessage["type"], (typeof SERVER_MESSAGE_TYPES)[number]>;
type MissingClientType = Exclude<ClientMessage["type"], (typeof CLIENT_MESSAGE_TYPES)[number]>;
/** Every payload-carrying connection frame must be classified as one, and be a real frame. */
type UnclassifiedConnectionType = Exclude<
  keyof typeof CONNECTION_BODIES,
  (typeof CONNECTION_LEVEL_MESSAGE_TYPES)[number]
>;
type UnwiredConnectionType = Exclude<keyof typeof CONNECTION_BODIES, ServerMessage["type"]>;
/**
 * The same pair for the CLIENT half. Both tables are checked rather than one, because a
 * connection-level frame that routing does not classify would be handed to a channel handle
 * that has no room to apply it to — the failure mode is silence, which no test notices.
 */
type UnclassifiedClientConnectionType = Exclude<
  keyof typeof CLIENT_CONNECTION_BODIES,
  (typeof CONNECTION_LEVEL_MESSAGE_TYPES)[number]
>;
type UnwiredClientConnectionType = Exclude<
  keyof typeof CLIENT_CONNECTION_BODIES,
  ClientMessage["type"]
>;
const serverInventoryComplete: MissingServerType extends never ? true : never = true;
const clientInventoryComplete: MissingClientType extends never ? true : never = true;
const connectionInventoryComplete: [
  UnclassifiedConnectionType,
  UnwiredConnectionType,
  UnclassifiedClientConnectionType,
  UnwiredClientConnectionType,
] extends [never, never, never, never]
  ? true
  : never = true;
void serverInventoryComplete;
void clientInventoryComplete;
void connectionInventoryComplete;

/** Body and wire unions must stay the same frame set: only routing separates them. */
type BodyWithoutFrame = Exclude<ServerMessageBody["type"], ServerMessage["type"]>;
type FrameWithoutBody = Exclude<ServerMessage["type"], ServerMessageBody["type"]>;
type ClientBodyWithoutFrame = Exclude<ClientMessageBody["type"], ClientMessage["type"]>;
type ClientFrameWithoutBody = Exclude<ClientMessage["type"], ClientMessageBody["type"]>;
const unionsAligned: [
  BodyWithoutFrame,
  FrameWithoutBody,
  ClientBodyWithoutFrame,
  ClientFrameWithoutBody,
] extends [never, never, never, never]
  ? true
  : never = true;
void unionsAligned;
